import { ApolloError, useQuery } from '@apollo/client';
import { getTokenData, logout } from '../services/auth';
import { UserMode } from './useApolloClient';
import { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { gql, UserPerms, userPermsFragment } from '@/modes/common/gql';

const GET_USER = gql(
  `
  query GetMe {
    me {
      id
      type
      name
      timezone
      features
      user {
        profileId
        type
        email
      }
    }

    session {
      userProfileId
      userProfile {
        id
        name
        user {
          profileId
          email
        }
      }
      companyProfileId
      companyProfile {
        id
        name
        type
        backflowTester {
          created
        }
        waterSupplier {
          profileId
          profile {
            name
            image {
              url
            }
          }
        }
      }
      perms {
        ...UserPermsFragment
      }
    }
  }
`,
  [userPermsFragment]
);

export type ProfileFeature = 'BACKFLOW' | 'WATER_HAZARDS' | 'RECYCLED_WATER';

type ProfileData = {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  name: string;
  timezone?: string | null;
  features?: ProfileFeature[] | null;
  user: {
    type: 'ADMIN' | 'NORMAL';
    email: string;
  } | null;
};

type SessionData = {
  userProfile?: {
    id: string;
    name: string | null;
    user?: { profileId: string; email: string } | null;
  } | null;
  companyProfile?: {
    id: string;
    name: string | null;
    type: 'INDIVIDUAL' | 'COMPANY';
    backflowTester?: {
      created: number | null;
    } | null;
    waterSupplier?: {
      profileId: string;
      profile?: {
        name: string | null;
        image?: { url: string } | null;
      } | null;
    } | null;
  } | null;
};

const emptyPerms: UserPerms = {
  assemblies: [],
  assignments: [],
  backflowTests: [],
  backflowTesters: [],
  billing: [],
  credentials: [],
  customers: [],
  gauges: [],
  hazardSurveys: [],
  jobs: [],
  notices: [],
  profiles: [],
  addresses: [],
  recycledWaterInspections: [],
  tags: [],
  vendors: [],
  users: [],
  waterSites: [],
  waterSuppliers: [],
  waterServices: [],
};

type PendingSessionInfo = {
  loading: true;
  isLoggedIn: undefined;
  isAdmin?: boolean;
  isCompany?: boolean;
  isWaterSupplier?: boolean;
  isBackflowTester: undefined;
  modes?: undefined;
  me?: ProfileData;
  session?: SessionData;
  error: undefined;
  perms: UserPerms;
  features: ProfileFeature[];
};

type LoggedOutSessionInfo = {
  loading: false;
  isLoggedIn: false;
  isAdmin: false;
  isCompany: false;
  isWaterSupplier: false;
  isBackflowTester: false;
  modes: [];
  me: undefined;
  session: undefined;
  error?: ApolloError;
  perms: UserPerms;
  features: ProfileFeature[];
};

type LoggedInSessionInfo = {
  loading: false;
  isLoggedIn: true;
  isAdmin: boolean;
  isCompany: boolean;
  isWaterSupplier: boolean;
  isBackflowTester: boolean;
  modes: UserMode[];
  me: ProfileData;
  session: SessionData;
  error?: ApolloError;
  perms: UserPerms;
  features: ProfileFeature[];
};

type SessionInfo =
  | PendingSessionInfo
  | LoggedOutSessionInfo
  | LoggedInSessionInfo;

export default (): SessionInfo => {
  const { data, error, loading } = useQuery(GET_USER, {
    onCompleted: (data) => {
      Sentry.setUser({
        fullName: data.me.name,
        email: data.me.user?.email ?? '',
        // username: data.me.user.email,
        id: data.me.id,
      });
    },
  });
  const sessionContext = useMemo(() => getTokenData(), []);

  if (loading) {
    return {
      loading: true,
      isLoggedIn: undefined,
      isAdmin: sessionContext?.modes.includes('ADMIN'),
      isCompany: sessionContext?.modes.includes('COMPANY'),
      isWaterSupplier: sessionContext?.modes.includes('WATER_SUPPLIER'),
      isBackflowTester: undefined,
      modes: undefined,
      features: [],
      me: sessionContext?.profile
        ? {
            ...sessionContext?.profile,
            id: sessionContext?.profile.id + '',
            type:
              sessionContext?.profile.id ===
              sessionContext?.session.companyProfileId
                ? 'COMPANY'
                : 'INDIVIDUAL',
            user: sessionContext?.user,
          }
        : undefined,
      session: sessionContext?.session
        ? {
            companyProfile: {
              id: sessionContext?.session.companyProfileId + '',
              name: '',
              type: 'COMPANY',
              waterSupplier: sessionContext?.modes.includes('WATER_SUPPLIER')
                ? {
                    profileId: sessionContext?.session.companyProfileId + '',
                  }
                : undefined,
            },
          }
        : undefined,
      error: undefined,
      perms: (sessionContext?.perms as UserPerms | undefined) ?? emptyPerms,
    };
  }

  if (error) {
    logout();
  }

  if (!data || error) {
    return {
      loading: false,
      isLoggedIn: false,
      isAdmin: false,
      isCompany: false,
      isWaterSupplier: false,
      isBackflowTester: false,
      modes: [],
      me: undefined,
      session: undefined,
      error,
      perms: emptyPerms,
      features: [],
    };
  }

  const { me, session } = data;

  const isCompany =
    !!session.companyProfile && session.companyProfile.type === 'COMPANY';
  const isWaterSupplier = isCompany && !!session.companyProfile?.waterSupplier;
  const isBackflowTester =
    isCompany && !!session.companyProfile?.backflowTester;
  const isAdmin = me.user?.type === 'ADMIN';
  const modes: UserMode[] = [];
  if (isAdmin) {
    modes.push('admin');
  }
  if (isWaterSupplier) {
    modes.push('water-supplier');
  }
  if (isCompany && isBackflowTester) {
    modes.push('company');
  }

  return {
    loading: false,
    isLoggedIn: true,
    isAdmin,
    isCompany,
    isWaterSupplier,
    isBackflowTester,
    modes,
    me,
    session,
    error,
    perms: session.perms as UserPerms,
    features: (me.features ?? []) as ProfileFeature[],
  };
};
